<template>
  <div class="schedule-answer-multiple-view text-xs">
    <table>
      <template v-for="(userSchedule, index) in userSchedules" :key="userSchedule.inquiry_id">
        <tr class="row flex header" v-if="index === 0">
          <!-- ヘッダー -->
          <td class="empty-cell user-name-col"></td>
          <td class="date-col" v-for="answer in userSchedule.answers" :key="answer.date">
            {{ formatDateWithDay(answer.date) }}
          </td>
          <td class="remark-col">メッセージ</td>
        </tr>

        <tr class="row flex body">
          <td class="user-name-col align-middle" v-if="userSchedule.is_applicant">
            <text-omit text="協議先" />
          </td>
          <td class="user-name-col align-middle" v-else>
            <text-omit
              :text="`${userSchedule.department_name} ${userSchedule.section_name} ${userSchedule.user_name}`"
            />
          </td>
          <td class="date-col" v-for="answer in userSchedule.answers" :key="answer.date">
            <jr-input-view>{{ answer.time_text }}</jr-input-view>
          </td>
          <td class="remark-col">
            <jr-input-view>
              <text-omit :text="userSchedule.remark" />
            </jr-input-view>
          </td>
        </tr>
      </template>
    </table>
  </div>
</template>
<script lang="ts">
import { defineComponent, watch, reactive, PropType } from 'vue'
import { AxiosResponse } from 'axios'

import JrInputView from '@/components/jr-parts/JrInputView.vue'
import TextOmit from '@/components/displays/TextOmit.vue'

import { ScheduleTypes } from '@/constants/enums'
import { formatDateWithDay } from '@/services/date'
import { ScheduleAnserGet, ScheduleAnswerDatePost, ScheduleCandidateDate } from '@/api'
import { ListRequest as AnswerListRequest } from '@/requests/scheduleAnswer'
import { ListRequest as CandidateListRequest } from '@/requests/schedule'

/**
 * 表示用候補日
 */
interface Schedule {
  time_text: string
  date: string
}

/**
 * ユーザー毎回答
 */
interface UserSchedule {
  inquiry_id: number
  answers: Schedule[]
  is_applicant: boolean
  user_name?: string | null
  remark?: string
}

interface CandidateDateId {
  id: number
  date: string
}

const TIME_TEXT = Object.freeze({
  [ScheduleTypes.ALL]: '終日OK',
  [ScheduleTypes.AM]: 'AMのみ',
  [ScheduleTypes.PM]: 'PMのみ',
  [ScheduleTypes.NONE]: 'NG',
} as { [key: string]: string })

/**
 * 候補日IDから日付文字列を取得する
 * @param candidateId 候補日ID
 * @param candidateDateId 候補日ID情報
 */
const findCandidateDate = (candidateId: number, candidateDateId: CandidateDateId[]): string | null => {
  const candidate = candidateDateId.find((candidate) => candidate.id === candidateId)
  return candidate != null ? candidate.date : null
}

/**
 * 回答配列から日付が一致する回答情報を取得する
 * @param date 日付文字列
 * @param answers 回答配列
 * @param candidateDateId 候補日ID情報
 */
const getAnswerDate = (
  date: string,
  answers: ScheduleAnswerDatePost[],
  candidateDateId: CandidateDateId[]
): Schedule => {
  const answer = answers.find((answer) => findCandidateDate(answer.schedule_candidate_date, candidateDateId) === date)
  return {
    date: date,
    time_text: answer != null ? TIME_TEXT[answer.time_type] : '',
  }
}

/**
 * 複数日程選択グループ(回答)
 * 表示のみ対応している
 */
export default defineComponent({
  name: 'ScheduleAnswerMultipleView',
  components: {
    JrInputView,
    TextOmit,
  },
  props: {
    messageIds: {
      type: Array as PropType<number[]>,
      required: true,
    },
  },
  setup(props) {
    const userSchedules = reactive<UserSchedule[]>([])

    watch(
      () => props.messageIds,
      async () => {
        const candidateIdDates: CandidateDateId[] = []
        // 各メッセージIDの候補日を取得し保持する
        for (const messageId of props.messageIds) {
          // 候補取得
          const candidateRequest = new CandidateListRequest(messageId)
          if (!(await candidateRequest.exec())) {
            // 候補取得失敗
            return
          }
          const { data } = candidateRequest.getResponse() as AxiosResponse<ScheduleCandidateDate[]>

          // 候補日IDと日付のobjerctを生成
          const datesById: CandidateDateId[] = data.map((candidate) => {
            return { id: candidate.schedule_candidate_date_id as number, date: candidate.date }
          })
          candidateIdDates.push(...datesById)
        }

        // 重複を排除して日付のみを保持
        const candidateDates: string[] = Array.from(new Set(candidateIdDates.map((candidate) => candidate.date)))

        // 日付を昇順で並び替え
        candidateDates.sort((before, after) => new Date(before).getTime() - new Date(after).getTime())

        const userAllSchedules: UserSchedule[] = []
        // 回答を取得する
        for (const messageId of props.messageIds) {
          // 回答取得
          const answerRequest = new AnswerListRequest(messageId)
          if (!(await answerRequest.exec())) {
            // 回答取得失敗
            return
          }
          const { data } = answerRequest.getResponse() as AxiosResponse<ScheduleAnserGet[]>

          // ユーザーの回答毎にまとめたものを保持していく
          userAllSchedules.push(
            ...data.map((userSchedule) => {
              // 候補日順に回答日リストを取得する
              const answers: Schedule[] = candidateDates.map((date) => {
                return getAnswerDate(date, userSchedule.schedule_answer_date, candidateIdDates)
              })
              return {
                inquiry_id: userSchedule.schedule_inquiry_id,
                is_applicant: userSchedule.is_applicant,
                department_name: userSchedule.department_name,
                section_name: userSchedule.section_name,
                user_name: userSchedule.user_name,
                remark: userSchedule.remark,
                answers: answers,
              } as UserSchedule
            })
          )
        }

        // 日程確定時、協議先、JR日程調整でユーザーが重複するため、重複分を削除
        userSchedules.splice(0)
        userSchedules.push(
          ...userAllSchedules.filter((element, index, self) => {
            return (
              self.findIndex((e) => {
                return e.inquiry_id === element.inquiry_id
              }) === index
            )
          })
        )
      },
      { immediate: true, deep: true }
    )

    return {
      userSchedules,
      formatDateWithDay,
    }
  },
})
</script>

<style lang="scss" scoped>
.schedule-answer-multiple-view {
  width: 100%;
  overflow-x: auto;
}

td,
td {
  flex-shrink: 0;
  border: 1px solid;
  border-color: #ebeef5;
  border-radius: 0;
}
.user-name-col {
  padding-left: 10px;
  width: 200px;
  /** 固定させる */
  position: sticky;
  left: 0;
  background: #fff;
  /** スクロールした際に若干ずれるため指定 */
  margin-left: -2px;
  &:before {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
  }
}
.date-col {
  width: 70px;
}
.remark-col {
  width: 300px;
}
</style>
